import React, { useContext, useEffect, useRef, useState } from 'react'
import AppContext from 'context/app'
import classNames from 'classnames'
import Button from 'components/button.js'
import paths from 'data/paths.json'

// Check if scroll button is needed
function checkIsOverflowing(element) {
    if(!element) return
    return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight
}

// Use paths.json for product categories
function getFilters({ pathObj, groupName, uppercase, lowercase }){
    return Object.keys(pathObj).map(key => {
        let x = pathObj[key]
        let filterName = key.replace('_', ' ')
        
        if(uppercase) filterName = filterName.toUpperCase()
        if(lowercase) filterName = filterName.toLowerCase()
        
        return {
            label: x.hasOwnProperty('path') ? `${filterName}` : `All ${groupName}`,
            value: x.hasOwnProperty('path') ? x.path : x
        }
    })
}

// "All" first then alphabetical
function mySort(arr){
    if(!arr || !arr.hasOwnProperty('length') || arr.length <= 1) return
    
    return arr.sort((x, y) => {
        let value = 0

        const a = x.label.replace(' ', '').toLowerCase()
        const b = y.label.replace(' ', '').toLowerCase()

        const aStartsWithAll = a.startsWith('all')
        const bStartsWithAll = b.startsWith('all')

        const oneStartsWithAll = aStartsWithAll || bStartsWithAll
        const bothStartWithAll = aStartsWithAll && bStartsWithAll

        if(typeof window === 'undefined') return
        const path = window.location.pathname.slice(0, -1)
        const isPageA = path === x.value
        const isPageB = path === y.value

        if(isPageA || isPageB){
            if(isPageA) value = -1
            else if(isPageB) value = 1
        } else if(bothStartWithAll){
            if(a < b) value = -1
            else if(a > b) value = 1
        } else if (oneStartsWithAll) {
            if(aStartsWithAll) value = -1
            if(bStartsWithAll) value = 1
        }      

        // console.log(a, b, value)
        return value
    })
}

function ScrollButton({ containerRef }){
    const { clientWidth } = useContext(AppContext)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)
    
    function handleScroll(e){
        // only reset if scroll was to the start
        if(e.target.scrollLeft) return
        setIsScrolled(false)
    }
    
    function handleScrollEnd(e){
        // 'scrollend' event fires at both beginning and end so only set isScrolled if scroll was to the end
        if(!e.target.scrollLeft) return
        setIsScrolled(true)
    }

    useEffect(() => {
        if(!containerRef || !containerRef.current) return
        
        containerRef?.current?.addEventListener('scroll', handleScroll)
        containerRef?.current?.addEventListener('scrollend', handleScrollEnd)
        
        return () => {
            containerRef?.current?.removeEventListener('scroll', handleScroll)
            containerRef?.current?.removeEventListener('scrollend', handleScrollEnd)
        }
    }, [containerRef])

    useEffect(() => {
        setIsOverflowing(checkIsOverflowing(containerRef.current))
    }, [clientWidth])

    if(!isOverflowing) return
    return(
        <Button
            onClick={() => containerRef?.current?.scroll({ left: isScrolled ? 0 : containerRef?.current?.scrollWidth, behavior: 'smooth' }) }
            className='after-0 p-absolute d-flex j-end a-center'
            style={{
                background: 'linear-gradient(to right, rgba(230 223 255 / 0) 0%, #E6E1DF 60%)',
                height: '100%',
                width: 80,
                top: 0,
                right: isScrolled ? 'calc(100% - 80px)' : 0,
                transform: isScrolled ? 'rotate(180deg)' : 'rotate(0deg)',
                border: 0,
                borderRadius: 0,
                transition: 'unset',
            }}
        >
            <img src='/svg/chevron-right-black.svg' alt='' height={16} />
        </Button>
    )
}

export default function Filters({ style, className }){
    const scrollContainerRef = useRef(null)

    const filters = mySort([
        ...getFilters({
            pathObj: paths.root.products,
            groupName: 'Products',
            uppercase: false,
        }),
    ])
    
    return(
        <div
            className={classNames(className, 'p-relative')}
            style={style}
        >
            <div
                ref={scrollContainerRef}
                className='d-flex f-nowrap overflow-auto scrollbar-0'
                style={{ gap: 12 }}
            >
                {filters.map(({ label, value }) => {
                    if(typeof window === 'undefined') return
                    const isCurrentPage = window.location.pathname.slice(0, -1) === value
                    const className = classNames('pill', { active: isCurrentPage })
                    
                    return <Button
                        key={label}
                        to={isCurrentPage ? null : value}
                        disabled={isCurrentPage}
                        className={className}
                    >
                        {label}
                    </Button>
                })}
            </div>

            <ScrollButton containerRef={scrollContainerRef} />
        </div>
    )
}
